import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { getComparator, Order } from "../../utils/orderTable";
import { createRow, Row } from "./Table/TableCustomUtils";
import { getHeaders, URI_API } from "../../utils/config";
import { AreaItem } from "../Header/HeaderUtils";
import { AppState } from "../../store/reducer";

interface NursingFunctionsContextType {
	rows: Row[];
	order: Order;
	orderBy: keyof Row;
	numSelected: number;
	rowCount: number;
	selectedRows: readonly string[];
	containerRef: React.RefObject<HTMLDivElement>;
	areas: AreaItem[];
	canScrollLeft: boolean;
	canScrollRight: boolean;
	isLoading: boolean;
	showDeleteModal: boolean;
	showModal: boolean;
	selectedAreaId: string;
	setSelectedAreaId: React.Dispatch<React.SetStateAction<string>>;
	setQuery: React.Dispatch<React.SetStateAction<string>>;
	canDelete: () => boolean;
	onChangeSort: (event: React.MouseEvent<unknown>, property: keyof Row) => void;
	onSelect: (selectedNurse: string) => void;
	onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
	setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
	setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
	handleScroll: (direction: "left" | "right") => void;
	reloadInformation: () => Promise<void>;
}

const NursingFunctionsContext = createContext<NursingFunctionsContextType | null>(null);

export function useNursingFunctionsContext() {
	const context = useContext(NursingFunctionsContext);
	if (!context) {
		throw new Error(
			"useNursingFunctionsContext debe usarse dentro de <NursignFunctionsProvider>",
		);
	}

	return context;
}

export function NursingFunctionsProvider({ children }: { children: any }) {
	const loggedUser = useSelector((state: AppState) => state.loggedUser);

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [query, setQuery] = useState<string>("");
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof Row>("nurseName");

	const [rows, setRows] = useState<Row[]>([]);
	const [selectedRows, setSelectedRows] = useState<readonly string[]>([]);

	const [areas, setAreas] = useState<AreaItem[]>([]);
	const [selectedAreaId, setSelectedAreaId] = useState<string>("");
	const containerRef = useRef<HTMLDivElement>(null);

	const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
	const [canScrollRight, setCanScrollRight] = useState<boolean>(true);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showModal, setShowModal] = useState<boolean>(false);

	const canDelete = () => selectedRows.length > 0;

	const getAssignedAreas = async () => {
		await fetch(`${URI_API}/areas_per_user`, getHeaders())
			.then((res) => res.json())
			.then((body) => setAreas(body));
	};

	const getAssignedFunctions = async () => {
		await fetch(`${URI_API}/assigned_functions/${loggedUser.hospital_id}`, getHeaders())
			.then((res) => res.json())
			.then((body) => {
				const newRows: Row[] = [];

				Object.entries(body).forEach(([_, details]) => {
					const {
						id,
						nurse_id,
						nurse_name,
						function_id,
						function_name,
						hospital_id,
						hospital_name,
						area_id,
						area_name,
						date,
						is_active,
					} = details as {
						id: string;
						nurse_id: string;
						nurse_name: string;
						function_id: string;
						function_name: string;
						hospital_id: string;
						hospital_name: string;
						area_id: string;
						area_name: string;
						date: string;
						is_active: string;
					};

					const newRow: Row = createRow(
						id,
						nurse_id,
						nurse_name,
						function_id,
						function_name,
						hospital_id,
						hospital_name,
						area_id,
						area_name,
						date,
					);
					newRows.push(newRow);
				});

				setRows(newRows);
			});
	};

	useEffect(() => {
		const loadInformation = async () => {
			Promise.all([await getAssignedFunctions(), await getAssignedAreas()]);
			setIsLoading(false);
		};

		loadInformation();
	}, []);

	const reloadInformation = async () => {
		getAssignedFunctions();
		setSelectedRows([]);
	};

	const onSelect = async (selectedNurse: string) => {
		setSelectedRows((prevNurses) => {
			if (prevNurses.includes(selectedNurse)) {
				return prevNurses.filter((prevNurse) => prevNurse !== selectedNurse);
			} else {
				return [...prevNurses, selectedNurse];
			}
		});
	};

	const onSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			const newSelect = visibleRows.map((row: Row) => row.id);
			setSelectedRows(newSelect);
		} else {
			setSelectedRows([]);
		}
	};

	const onChangeSort = (event: React.MouseEvent<unknown>, property: keyof Row) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const filteredRows = [...rows]
		.filter((row: Row) => {
			if (selectedAreaId === "") {
				return row;
			} else if (row.areaId === selectedAreaId) {
				return row;
			}
		})
		.filter((row: Row) => {
			if (query === "") {
				return row;
			} else if (
				row.nurseName.toUpperCase().includes(query.toUpperCase()) ||
				row.areaName.toUpperCase().includes(query.toUpperCase()) ||
				row.functionName.toUpperCase().includes(query.toUpperCase())
			) {
				return row;
			}
		});

	const visibleRows = useMemo(
		() => [...filteredRows].sort(getComparator(order, orderBy)),
		[rows, order, orderBy, query, selectedAreaId],
	);

	const checkScroll = () => {
		if (!containerRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

		setCanScrollLeft(scrollLeft > 0);
		setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
	};

	const handleScroll = (direction: "left" | "right") => {
		if (!containerRef.current) return;

		const container: HTMLDivElement = containerRef.current;

		const scrollAmount = container.clientWidth;

		container.scrollBy({
			left: direction === "left" ? -scrollAmount : scrollAmount,
			behavior: "smooth",
		});

		setTimeout(checkScroll, 150);
	};

	const contextValue = {
		rows: visibleRows,
		order,
		orderBy,
		numSelected: selectedRows.length,
		rowCount: rows.length,
		selectedRows,
		containerRef,
		areas,
		canScrollLeft,
		canScrollRight,
		isLoading,
		showDeleteModal,
		showModal,
		selectedAreaId,
		setSelectedAreaId,
		setQuery,
		canDelete,
		onChangeSort,
		onSelect,
		onSelectAll,
		setShowDeleteModal,
		setShowModal,
		handleScroll,
		reloadInformation,
	};

	return (
		<NursingFunctionsContext.Provider value={contextValue}>
			{children}
		</NursingFunctionsContext.Provider>
	);
}
